import dayjs from 'dayjs';
import Highcharts from 'highcharts/highstock';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { t } from 'ttag';
import type { FullEventChartProps } from './eventDetailsTab.types';

/**
 * Renders the full-event Highcharts column chart of minutely heart rates and keeps its day selection in sync with the parent. On mount it maps the minutely HR array onto one column per day from today, builds a stock chart whose navigator only appears past 20 columns, colours the clicked column while fading the previous one and reports its index through updateChartData, opens on a 20-day window, and binds the arrow-key handler that is removed again on cleanup.
 *
 * @param viewFullEventData - Full-event data whose minutelyHrsDataArray feeds the columns.
 * @param updateChartData - Callback notified with the index of the newly selected column.
 * @returns Nothing.
 */
export const useFullEventChart = ({ viewFullEventData, updateChartData }: FullEventChartProps) => {
  const chartRef = useRef<any>(null);
  const previousSelectedColumnRef = useRef<any>(null);
  const firstEpochRef = useRef(0);
  const currentSelectedIndexRef = useRef(0);
  const currentSelectedEpochRef = useRef(0);
  const isColoringColumnRef = useRef(false);

  /**
   * Moves the full-event chart's day selection one column left or right on the arrow keys. Guards repeats with a 500 ms colouring lock, shifts the selected epoch by a day while staying inside the data bounds, recolours the previous and new columns, pans the x-axis extremes by ten days when the new column falls outside the visible window, and reports the new index through updateChartData.
   *
   * @param e - Keyboard event whose keyCode selects the previous (37) or next (39) column.
   * @returns Nothing.
   */
  const handleKeyDown = (e: any) => {
    const { keyCode } = e;
    const { minutelyHrsDataArray } = viewFullEventData;
    const dataLength = minutelyHrsDataArray.length;
    switch (keyCode) {
      case 37: {
        // *: Prev arrow key
        e.preventDefault();

        if (isColoringColumnRef.current) {
          return;
        }

        isColoringColumnRef.current = true;
        setTimeout(() => {
          isColoringColumnRef.current = false;
        }, 500);

        const newSelectedEpoch = currentSelectedEpochRef.current - 86400000;
        if (newSelectedEpoch >= firstEpochRef.current) {
          currentSelectedEpochRef.current = newSelectedEpoch;

          chartRef.current.series[0].data[currentSelectedIndexRef.current].update({
            color: 'rgba(77, 146, 223, 0.5)',
          });
          currentSelectedIndexRef.current -= 1;

          if (newSelectedEpoch < chartRef.current.xAxis[0].min) {
            const newMin = chartRef.current.xAxis[0].min - 10 * 86400000;
            const newMax = chartRef.current.xAxis[0].max - 10 * 86400000;
            chartRef.current.xAxis[0].setExtremes(newMin, newMax);

            // *: Wait until setExtremes completed (afterSetExtremes event)
          } else {
            // *: Selected new column
            chartRef.current.series[0].data[currentSelectedIndexRef.current].update({
              color: '#4D92DF',
            });

            previousSelectedColumnRef.current =
              chartRef.current.series[0].data[currentSelectedIndexRef.current];
          }

          updateChartData(currentSelectedIndexRef.current);
        }

        break;
      }
      case 39: {
        // *: Next arrow key
        e.preventDefault();

        if (isColoringColumnRef.current) {
          return;
        }

        isColoringColumnRef.current = true;
        setTimeout(() => {
          isColoringColumnRef.current = false;
        }, 500);

        setTimeout(() => {
          const newSelectedEpoch = currentSelectedEpochRef.current + 86400000;
          if (newSelectedEpoch <= firstEpochRef.current + (dataLength - 1) * 86400000) {
            currentSelectedEpochRef.current = newSelectedEpoch;

            chartRef.current.series[0].data[currentSelectedIndexRef.current].update({
              color: 'rgba(77, 146, 223, 0.5)',
            });
            currentSelectedIndexRef.current += 1;

            if (newSelectedEpoch > chartRef.current.xAxis[0].max) {
              const newMin = chartRef.current.xAxis[0].min + 10 * 86400000;
              const newMax = chartRef.current.xAxis[0].max + 10 * 86400000;
              chartRef.current.xAxis[0].setExtremes(newMin, newMax);

              // *: Wait until setExtremes completed (afterSetExtremes event)
            } else {
              // *: Selected new column
              chartRef.current.series[0].data[currentSelectedIndexRef.current].update({
                color: '#4D92DF',
              });

              previousSelectedColumnRef.current =
                chartRef.current.series[0].data[currentSelectedIndexRef.current];
            }

            updateChartData(currentSelectedIndexRef.current);
          }
        }, 500);

        break;
      }
      default: {
        break;
      }
    }
  };

  useEffect(() => {
    const { minutelyHrsDataArray } = viewFullEventData;

    firstEpochRef.current = dayjs().startOf('d').valueOf();
    currentSelectedEpochRef.current = firstEpochRef.current;
    /**
     * Pairs each minutely heart-rate value with a timestamp so the series can be plotted on a time axis. Maps minutelyHrsDataArray into [epoch, value] tuples, spacing entries one day (86400000 ms) apart from the first epoch reference.
     *
     * @returns Tuples of timestamp and heart-rate value.
     */
    const dataProvider = _.map(minutelyHrsDataArray, (minutelyHr, index) => [
      firstEpochRef.current + index * 86400000,
      minutelyHr,
    ]);

    /**
     * Turns the timestamp and heart-rate tuples into the point objects of the full-event column series. Each point carries the dimmed default colour, its index as name, x and y taken from the tuple, and a click handler that highlights the clicked point, resets the previously selected one and reports the new index through updateChartData.
     *
     * @returns Chart point configuration objects.
     */
    const data = _.map(dataProvider, (d, index) => ({
      color: 'rgba(77, 146, 223, 0.5)',
      name: index,
      events: {
        click: (e: any) => {
          if (e.point.index !== currentSelectedIndexRef.current) {
            currentSelectedIndexRef.current = e.point.index;
            currentSelectedEpochRef.current = e.point.category;

            e.point.update({ color: '#4D92DF' });

            if (previousSelectedColumnRef.current) {
              previousSelectedColumnRef.current.update({ color: 'rgba(77, 146, 223, 0.5)' });
            }
            previousSelectedColumnRef.current = e.point;

            updateChartData(index);
          }
        },
      },
      x: d[0],
      y: d[1],
    }));

    const isShowNavigator = data.length > 20;

    // *: Create the chart
    chartRef.current = Highcharts.stockChart('container', {
      chart: {
        alignTicks: false,
        height: isShowNavigator ? 130 : 100,
        marginTop: isShowNavigator ? 40 : 5,
      },
      credits: {
        enabled: false,
      },
      navigator: {
        enabled: isShowNavigator,
        handles: {
          enabled: false,
        },
        height: 25,
        series: {
          type: 'column',
        },
        top: 5,
        xAxis: {
          visible: false,
        },
      },
      rangeSelector: {
        enabled: false,
      },
      scrollbar: {
        enabled: false,
      },
      series: [
        {
          type: 'column',
          cursor: 'pointer',
          data,
          turboThreshold: dataProvider.length, // *: Define number of data item are allowed
        },
      ],
      tooltip: {
        enabled: false,
      },
      xAxis: {
        crosshair: {
          width: 0,
        },
        events: {
          afterSetExtremes: () => {
            chartRef.current.series[0].data[currentSelectedIndexRef.current].update({
              color: '#4D92DF',
            });

            previousSelectedColumnRef.current =
              chartRef.current.series[0].data[currentSelectedIndexRef.current];
          },
        },
        range: 24 * 3600 * 1000 * 20, // *: Display 20 columns
        visible: false,
      },
      yAxis: {
        height: 75,
        lineWidth: 1,
        opposite: false, // *: Whether to display the axis on the opposite side of the normal
        tickInterval: 20,
      },
    } as any);

    chartRef.current.xAxis[0].setExtremes(
      firstEpochRef.current,
      firstEpochRef.current + 19 * 86400000,
    );
    chartRef.current.renderer?.box?.setAttribute('aria-label', t`Heart rate by day`);

    // *: Press next/prev arrow key event
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
};
